import React from 'react';
import pt from 'prop-types';

import { countryOptions } from 'utils/geo';
import { getPartnerPageURL } from 'utils/partner';
import { BUDGET_OPTIONS } from 'partners/shared/budget';
import { PARTNER_CAPABILITIES } from 'partners/shared/capabilities';

import SectionHeader from 'components/section-header';
import { Field } from 'components/form';
import LivePageLink from 'components/link/live-page';

import PartnerLocales from './locales';
import PartnerApps from './apps';

import './partners.scss';

const DEFAULT_CURRENCIES = [{ code: 'USD', name: 'US Dollar' }];

const ARR = [];

export default class PartnerProfileForm extends React.PureComponent {
  static propTypes = {
    values: pt.object,
    apps: pt.array,
    errors: pt.object,
    onChangePartnerName: pt.func,
    listingStatus: pt.string,
    header: pt.node,
    fullWidth: pt.bool,
  };

  static childContextTypes = {
    client: pt.object,
    account: pt.object,
  };

  state = {
    taglineLength: 0,
  };

  getChildContext() {
    const { values = {} } = this.props;
    const { partner = {} } = values;
    const currencies = (partner.currencies || []).length
      ? partner.currencies
      : DEFAULT_CURRENCIES;

    return {
      client: {
        locale: 'en-US',
        currency: 'USD',
        locales: [],
        pricedCurrencies: currencies,
      },
      account: {
        hasPricedCurrencies: true,
      },
    };
  }

  onChangeTagline = (_event, value) => {
    this.setState({ taglineLength: (value || '').length });
  };

  render() {
    const {
      values = {},
      apps,
      errors = {},
      onChangePartnerName,
      listingStatus,
      header,
      fullWidth = true,
    } = this.props;
    const { taglineLength } = this.state;
    const { partner = {} } = values;
    const { business = {}, social = {} } = partner;

    const fieldsetClass = fullWidth
      ? 'full partner-profile-form'
      : 'partner-profile-form';

    return (
      <>
        <fieldset className={fieldsetClass}>
          <div className="view-body-section">
            <div className="view-body-subheader partner-details-subheader">
              {header || (
                <SectionHeader
                  className="view-body-subtitle"
                  title="Partner details"
                  subtitle="This information is presented in the partner directory and partner profile"
                />
              )}

              {partner.id && (
                <LivePageLink to={getPartnerPageURL(partner.id)}>
                  {listingStatus === 'listed' ? 'View live page' : 'View page'}
                </LivePageLink>
              )}
            </div>

            <div className="view-section-content">
              <div className="row profile-images-row">
                <div className="logo-icon-field">
                  <Field
                    type="image"
                    label={
                      <>
                        Logo icon
                        <div className="field-upload-hint">1:1 ratio</div>
                      </>
                    }
                    name="partner.icon"
                    className="span4"
                    minWidth={240}
                    maxWidth={240}
                    minHeight={240}
                    maxHeight={240}
                    defaultValue={partner.icon}
                  />
                </div>

                <div className="cover-image-field">
                  <Field
                    type="image"
                    label={
                      <>
                        Cover image
                        <div className="field-upload-hint">
                          1600×1200 pixels
                        </div>
                      </>
                    }
                    name="partner.image"
                    className="span4"
                    minWidth={320}
                    maxWidth={320}
                    minHeight={240}
                    maxHeight={240}
                    defaultValue={partner.image}
                  />
                </div>
              </div>

              <div className="row">
                <Field
                  type="text"
                  name="partner.name"
                  label="Partner name"
                  defaultValue={partner.name}
                  required={true}
                  className="span4"
                  onChange={onChangePartnerName}
                  error={errors.name}
                />
              </div>

              <div className="row">
                <Field
                  type="text"
                  name="partner.tagline"
                  label={
                    <span>
                      Tagline{' '}
                      <span className="field-char-count">
                        {taglineLength}/40
                      </span>
                    </span>
                  }
                  defaultValue={partner.tagline}
                  className="span4"
                  maxLength={40}
                  onChange={this.onChangeTagline}
                  placeholder="Headless storefronts for DTC brands"
                  hint="Short sentence that describes what you do."
                />
              </div>

              <div className="row">
                <Field
                  type="editor"
                  name="partner.description"
                  label="Description"
                  defaultValue={partner.description}
                  className="span4"
                  hint="Tell merchants who you are, what you specialise in, and the types of projects you take on."
                />
              </div>

              <div className="row">
                <Field
                  type="text"
                  name="partner.support_email"
                  label="Email"
                  defaultValue={partner.support_email}
                  rules="required, email"
                  className="span2"
                />

                <Field
                  type="text"
                  name="partner.business.website"
                  label="Website"
                  defaultValue={business.website}
                  required={true}
                  rules="url"
                  className="span2"
                />
              </div>

              <div className="row">
                <Field
                  type="select"
                  name="partner.business.country"
                  label="Country"
                  defaultValue={business.country}
                  options={countryOptions}
                  className="span2"
                />

                <Field
                  type="select"
                  name="partner.budget"
                  label="Typical budget"
                  className="span2"
                  options={BUDGET_OPTIONS}
                  defaultValue={partner.budget}
                />
              </div>

              <div className="row">
                <div>
                  <div
                    id="capability-checklist"
                    className="settings-capability-checklist"
                  >
                    <div className="form-field-label">Expertise</div>

                    <div className="form-field-hint mt-0">
                      Select all that apply to your business
                    </div>
                  </div>

                  <Field
                    type="checkbox"
                    name="partner.capabilities"
                    aria-labelledby="capability-checklist"
                    options={PARTNER_CAPABILITIES}
                    defaultValue={partner.capabilities}
                    stacked
                  />
                </div>
              </div>

              <div className="row">
                <Field
                  type="tags"
                  name="partner.tags"
                  label="Tags"
                  defaultValue={partner.tags || ARR}
                  className="span4"
                  hint="Separate tags by a comma or click enter"
                  help="Tags are used to improve partner search and discovery"
                  editableTags
                />
              </div>

              <div className="row">
                <Field
                  type="tags"
                  name="partner.industries"
                  label="Industries"
                  defaultValue={partner.industries || ARR}
                  className="span4"
                  placeholder="e.g. Tech, Web3, VC-backed startups"
                  hint="Separate tags by a comma or click enter"
                  editableTags
                />
              </div>

              <div className="row">
                <div className="span4">
                  <PartnerLocales defaultValue={partner.languages || ARR} />
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className={fieldsetClass}>
          <PartnerApps apps={apps || ARR} />
        </fieldset>

        <fieldset className={fieldsetClass}>
          <div className="view-body-section">
            <div className="view-body-subheader">
              <SectionHeader
                className="view-body-subtitle"
                title="Social media"
                subtitle="Include your social handles"
              />
            </div>

            <div className="view-section-content">
              <div className="row">
                <Field
                  type="text"
                  name="partner.social.twitter"
                  label="X"
                  defaultValue={social.twitter}
                  className="span2"
                  placeholder="Optional"
                />

                <Field
                  type="text"
                  name="partner.social.linkedin"
                  label="LinkedIn"
                  defaultValue={social.linkedin}
                  className="span2"
                  placeholder="Optional"
                />
              </div>

              <div className="row">
                <Field
                  type="text"
                  name="partner.social.instagram"
                  label="Instagram"
                  defaultValue={social.instagram}
                  className="span2"
                  placeholder="Optional"
                />

                <Field
                  type="text"
                  name="partner.social.youtube"
                  label="YouTube"
                  defaultValue={social.youtube}
                  className="span2"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        </fieldset>
      </>
    );
  }
}
